import { useCallback, useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, AlertTriangle, Clock,
  RefreshCw, Bug, Database,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminApi } from "../lib/adminAuth";

function relativeTime(iso) {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const diffSec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (diffSec < 45) return "just now";
  if (diffSec < 60 * 45) return `${Math.max(1, Math.round(diffSec / 60))} min ago`;
  if (diffSec < 60 * 60 * 22) return `${Math.round(diffSec / 3600)} hr ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
}

function Tile({ testid, label, value, sub, tone = "slate" }) {
  const TONES = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${TONES[tone]}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
      {sub && <p className="mt-1 text-[11px] opacity-70">{sub}</p>}
    </div>
  );
}

export default function AdminErrorLog() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?kind=${filter}`;
      const [s, r] = await Promise.all([
        adminApi().get("/admin/v2/error-log/summary?days=1"),
        adminApi().get(`/admin/v2/error-log/recent${params}`),
      ]);
      setSummary(s.data);
      setRows(r.data?.rows || []);
      setError("");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error(`Failed to load error log: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const topErrorTypes = useMemo(() => {
    if (!summary?.top_error_types) return [];
    return Object.entries(summary.top_error_types);
  }, [summary]);

  const topSlowCollections = useMemo(() => {
    if (!summary?.top_slow_collections) return [];
    return Object.entries(summary.top_slow_collections);
  }, [summary]);

  return (
    <main
      data-testid="admin-error-log-page"
      className="min-h-screen bg-[#fdf8f3] px-6 py-10"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
              Backend errors — last 24h
            </h1>
            <p className="mt-1 text-sm text-stone-600">
              Unhandled exceptions + Mongo commands above{" "}
              {summary?.threshold_ms ?? 1000}ms. Every entry has a
              trace_id matching the <code className="rounded bg-stone-100 px-1 font-mono text-[11px]">X-Trace-Id</code>{" "}
              header on the failed response.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="error-log-refresh"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>

        <section className="mt-8 grid gap-3 md:grid-cols-4">
          <Tile
            testid="errlog-stat-total"
            tone={(summary?.total || 0) > 0 ? "amber" : "slate"}
            label="Total events"
            value={summary?.total ?? "—"}
            sub={summary ? `Rolling ${summary.window_days}-day window` : ""}
          />
          <Tile
            testid="errlog-stat-exceptions"
            tone={(summary?.exceptions || 0) > 0 ? "rose" : "slate"}
            label="Exceptions"
            value={summary?.exceptions ?? "—"}
            sub="Unhandled errors"
          />
          <Tile
            testid="errlog-stat-slow"
            tone={(summary?.slow_queries || 0) > 0 ? "amber" : "slate"}
            label="Slow Mongo queries"
            value={summary?.slow_queries ?? "—"}
            sub={`> ${summary?.threshold_ms ?? 1000}ms`}
          />
          <Tile
            testid="errlog-stat-trace"
            tone="slate"
            label="Latest event"
            value={rows[0]?.ts ? relativeTime(rows[0].ts) : "—"}
            sub={rows[0]?.kind || ""}
          />
        </section>

        {(topErrorTypes.length > 0 || topSlowCollections.length > 0) && (
          <section data-testid="errlog-breakdowns" className="mt-6 grid gap-3 md:grid-cols-2">
            {topErrorTypes.length > 0 && (
              <div className="rounded-2xl border border-stone-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                  Top error types
                </p>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {topErrorTypes.map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between gap-2">
                      <code className="font-mono text-xs text-stone-800">{k}</code>
                      <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200">
                        {v}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {topSlowCollections.length > 0 && (
              <div className="rounded-2xl border border-stone-200 bg-white p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                  Slowest collections
                </p>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {topSlowCollections.map(([k, v]) => (
                    <li key={k} className="flex items-center justify-between gap-2">
                      <code className="font-mono text-xs text-stone-800">{k}</code>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-amber-200">
                        {v}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <section className="mt-6 rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-stone-200 p-4">
            <p className="text-sm font-semibold text-stone-900">Recent events</p>
            <div className="flex items-center gap-1 text-xs">
              {["all", "exception", "slow_query"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  data-testid={`errlog-filter-${k}`}
                  className={
                    "rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider " +
                    (filter === k
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-700 hover:bg-stone-200")
                  }
                >
                  {k.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          {error && (
            <p data-testid="errlog-error" className="m-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
          {!error && rows.length === 0 && !loading && (
            <p data-testid="errlog-empty" className="p-6 text-center text-sm text-stone-500">
              Nothing to see here — your backend is behaving. 🎉
            </p>
          )}
          {rows.length > 0 && (
            <ul className="divide-y divide-stone-100">
              {rows.map((r) => (
                <li
                  key={r.id}
                  data-testid={`errlog-row-${r.id}`}
                  className="grid grid-cols-[auto_1fr_auto] items-start gap-3 px-4 py-3"
                >
                  <div className="pt-0.5">
                    {r.kind === "exception"
                      ? <Bug className="h-4 w-4 text-rose-600" />
                      : <Database className="h-4 w-4 text-amber-600" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900">
                      {r.kind === "exception"
                        ? <code className="font-mono">{r.error_type}</code>
                        : <>Slow {r.command} on{" "}
                            <code className="font-mono">{r.collection || r.database}</code>
                          </>}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-stone-600">
                      {r.kind === "exception"
                        ? <>
                            <code className="font-mono">{r.method} {r.path}</code>
                            {r.error_msg ? <> — {r.error_msg}</> : null}
                          </>
                        : <>
                            {r.duration_ms?.toFixed(0)}ms · {r.result}
                            {r.error_msg ? <> · {r.error_msg}</> : null}
                          </>}
                    </p>
                    {r.kind === "exception" && r.trace_id && (
                      <p className="mt-1 text-[11px] text-stone-500">
                        trace_id <code className="rounded bg-stone-100 px-1 font-mono text-[10px]">{r.trace_id}</code>
                      </p>
                    )}
                  </div>
                  <div className="text-right text-[11px] text-stone-500">
                    <Clock className="ml-auto h-3 w-3" />
                    <span>{relativeTime(r.ts)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
